<template>
  <div>
    <textarea
      v-bind="$attrs"
      v-model="text"
      type="text"
      class="form-control"
      autocomplete="nope"
      :disabled="disabled"
      @blur="checkText"
    />
    <span
      v-if="!!invalidErrorMessage"
      class="help-block text-red"
    >
      {{ invalidErrorMessage }}
    </span>
  </div>
</template>

<script setup lang="ts">
import { computed, WritableComputedRef, watch, ref } from 'vue';
import DOMPurify from 'dompurify';
import he from 'he';

const props = defineProps({
  modelValue: {
    type: String,
    default: '',
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  // Opt-in: strip markup like SanitizedInput but keep non-ASCII text (curly quotes, dashes, %).
  allowUnicode: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['update:modelValue']);

const isValid = ref(true);
const invalidErrorMessage = ref('');

const text: WritableComputedRef<string> = computed({
  get() {
    return props.modelValue;
  },
  set(value) {
    emit('update:modelValue', value);
  },
});

watch(text, (value?: string | null) => {
  if (props.allowUnicode) {
    emit('update:modelValue', value ? he.decode(DOMPurify.sanitize(value, { ALLOWED_TAGS: [] })) : value);
    return;
  }

  // remove invalid characters
  const cleanedString = value?.replace(/[^a-zA-Z0-9-._~:/[\]?#@!$&'()*+,;=\r\n {}"]/g, '');
  emit('update:modelValue', cleanedString);
});

const checkText = () => {
  if (!text.value) {
    isValid.value = true;
    invalidErrorMessage.value = '';
  }
};

watch(
  text,
  () => {
    if (!props.disabled) checkText();
  },
  {
    immediate: true,
  },
);
</script>
